<template>
	<div ref="zoneRef" class="appointment-pin-zone">
		<label v-if="visible" class="appointment-pin-zone__card" :class="{ 'appointment-pin-zone__card--active': pinned }">
			<v-icon class="appointment-pin-zone__icon" size="20">keep</v-icon>
			<span class="appointment-pin-zone__text">
				<strong>{{ t('PinVisit') }}</strong>
				<span class="appointment-pin-zone__hint">{{ t('PinVisitHint') }}</span>
			</span>
			<v-switch :model-value="pinned" color="primary" density="compact" inset hide-details @update:model-value="toggle" />
		</label>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { useAppointmentStore } from '@/stores/AppointmentStore';
import { toDayKey, isOptimizableDay } from '@/utils/legacy/agendaRouteSource';
import { rerenderLegacyAgenda } from '@/utils/legacy/agendaWriter';

const t = useTranslator();
const store = useAppointmentStore();
const zoneRef = ref(null);
const context = ref(null);
const draftPinned = ref(false);
let mountTarget = null;

const visible = computed(() => !!context.value?.visible && !!context.value.start && isOptimizableDay(context.value.start));
const pinned = computed(() => (context.value?.isNew ? draftPinned.value : store.isPinned(context.value?.uid)));

function pin(uid, start, value) {
	store.setPinned(uid, toDayKey(start), value);
	rerenderLegacyAgenda();
}

function handleContext(event) {
	const next = event?.detail ?? null;
	if (next?.session !== context.value?.session) draftPinned.value = false;
	context.value = next;
}

function handleSaved(event) {
	const { uid, start } = event?.detail ?? {};
	if (!uid || !start || !context.value?.isNew || !draftPinned.value || !visible.value) return;
	draftPinned.value = false;
	pin(uid, start, true);
}

function toggle(value) {
	if (context.value?.isNew) {
		draftPinned.value = !!value;
		return;
	}
	if (!context.value?.uid) return;
	pin(context.value.uid, context.value.start, !!value);
}

onMounted(() => {
	mountTarget = zoneRef.value?.parentElement;
	mountTarget?.addEventListener('vue:appointment-pin-context', handleContext);
	mountTarget?.addEventListener('vue:appointment-pin-saved', handleSaved);
	if (mountTarget?._pendingPinContext) handleContext({ detail: mountTarget._pendingPinContext });
});

onBeforeUnmount(() => {
	mountTarget?.removeEventListener('vue:appointment-pin-context', handleContext);
	mountTarget?.removeEventListener('vue:appointment-pin-saved', handleSaved);
	mountTarget = null;
});
</script>
